
import React from "react";

const ProductIdentity = ({
    data = {},
    onChange
}) => {

    const identity = {
        name: "",
        code: "",
        shortDescription: "",
        detailedDescription: "",
        categories: [],
        packaging: [],
        variants: [],
        ...data
    };

    const updateField = (field, value) => {
        onChange({
            ...identity,
            [field]: value
        });
    };

    // ========================================
    // CATEGORIES
    // ========================================

    const addCategory = () => {
        updateField("categories", [
            ...identity.categories,
            {
                name: "",
                subcategories: []
            }
        ]);
    };

    const updateCategory = (index, field, value) => {
        updateField(
            "categories",
            identity.categories.map((item, i) =>
                i === index
                    ? { ...item, [field]: value }
                    : item
            )
        );
    };

    const removeCategory = (index) => {
        updateField(
            "categories",
            identity.categories.filter((_, i) => i !== index)
        );
    };

    const addSubcategory = (categoryIndex) => {
        const categories = identity.categories.map((item, i) =>
            i === categoryIndex
                ? {
                    ...item,
                    subcategories: [
                        ...(item.subcategories || []),
                        ""
                    ]
                }
                : item
        );

        updateField("categories", categories);
    };

    const updateSubcategory = (
        categoryIndex,
        subcategoryIndex,
        value
    ) => {
        const categories = identity.categories.map((item, i) =>
            i === categoryIndex
                ? {
                    ...item,
                    subcategories: (item.subcategories || []).map(
                        (subcategory, j) =>
                            j === subcategoryIndex
                                ? value
                                : subcategory
                    )
                }
                : item
        );

        updateField("categories", categories);
    };

    const removeSubcategory = (
        categoryIndex,
        subcategoryIndex
    ) => {
        const categories = identity.categories.map((item, i) =>
            i === categoryIndex
                ? {
                    ...item,
                    subcategories: (item.subcategories || []).filter(
                        (_, j) => j !== subcategoryIndex
                    )
                }
                : item
        );

        updateField("categories", categories);
    };

    // ========================================
    // PACKAGING
    // ========================================

    const addPackaging = () => {
        updateField("packaging", [
            ...identity.packaging,
            {
                type: "",
                quantity: null,
                unit: "",
                description: ""
            }
        ]);
    };

    const updatePackaging = (index, field, value) => {
        updateField(
            "packaging",
            identity.packaging.map((item, i) =>
                i === index
                    ? { ...item, [field]: value }
                    : item
            )
        );
    };

    const removePackaging = (index) => {
        updateField(
            "packaging",
            identity.packaging.filter((_, i) => i !== index)
        );
    };

    // ========================================
    // VARIANTS
    // ========================================

    const addVariant = () => {
        updateField("variants", [
            ...identity.variants,
            {
                name: "",
                code: "",
                description: "",
                attributes: []
            }
        ]);
    };

    const updateVariant = (index, field, value) => {
        updateField(
            "variants",
            identity.variants.map((item, i) =>
                i === index
                    ? { ...item, [field]: value }
                    : item
            )
        );
    };

    const removeVariant = (index) => {
        updateField(
            "variants",
            identity.variants.filter((_, i) => i !== index)
        );
    };

    // ========================================
    // VARIANT ATTRIBUTES
    // ========================================

    const addAttribute = (variantIndex) => {
        const variant = identity.variants[variantIndex];

        updateVariant(
            variantIndex,
            "attributes",
            [
                ...(variant.attributes || []),
                {
                    name: "",
                    value: "",
                    unit: ""
                }
            ]
        );
    };

    const updateAttribute = (
        variantIndex,
        attributeIndex,
        field,
        value
    ) => {
        const variant = identity.variants[variantIndex];

        const attributes = (variant.attributes || []).map(
            (attribute, i) =>
                i === attributeIndex
                    ? {
                        ...attribute,
                        [field]: value
                    }
                    : attribute
        );

        updateVariant(
            variantIndex,
            "attributes",
            attributes
        );
    };

    const removeAttribute = (
        variantIndex,
        attributeIndex
    ) => {
        const variant = identity.variants[variantIndex];

        updateVariant(
            variantIndex,
            "attributes",
            (variant.attributes || []).filter(
                (_, i) => i !== attributeIndex
            )
        );
    };

    // ========================================
    // STYLES
    // ========================================

    const inputClass =
        "w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300";

    const labelClass =
        "block text-sm font-medium text-gray-700 mb-2";

    const addButtonClass =
        "px-3 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700";

    const removeButtonClass =
        "px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm rounded hover:bg-red-100";

    // ========================================
    // RENDER
    // ========================================

    return (
        <div className="space-y-10">

            {/* BASIC INFORMATION */}

            <section className="space-y-5">

                <h3 className="text-base font-semibold text-gray-900 border-b pb-3">
                    Basic Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                    <div>
                        <label className={labelClass}>
                            Product Name *
                        </label>

                        <input
                            type="text"
                            value={identity.name || ""}
                            onChange={(e) =>
                                updateField("name", e.target.value)
                            }
                            placeholder="e.g. RAZON PWR"
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <label className={labelClass}>
                            Product Code
                        </label>

                        <input
                            type="text"
                            value={identity.code || ""}
                            onChange={(e) =>
                                updateField("code", e.target.value)
                            }
                            placeholder="e.g. PWR"
                            className={inputClass}
                        />
                    </div>

                </div>

                <div>
                    <label className={labelClass}>
                        Short Description
                    </label>

                    <textarea
                        value={identity.shortDescription || ""}
                        onChange={(e) =>
                            updateField(
                                "shortDescription",
                                e.target.value
                            )
                        }
                        rows={3}
                        placeholder="Brief description of the product"
                        className={inputClass}
                    />
                </div>

                <div>
                    <label className={labelClass}>
                        Detailed Description
                    </label>

                    <textarea
                        value={identity.detailedDescription || ""}
                        onChange={(e) =>
                            updateField(
                                "detailedDescription",
                                e.target.value
                            )
                        }
                        rows={6}
                        placeholder="Detailed product overview, composition and purpose"
                        className={inputClass}
                    />
                </div>

            </section>

            {/* CATEGORIES */}

            <section className="space-y-5">

                <div className="flex justify-between items-center gap-3 border-b pb-3">

                    <div>
                        <h3 className="text-base font-semibold text-gray-900">
                            Categories & Subcategories
                        </h3>

                        <p className="text-xs text-gray-500 mt-1">
                            A product can belong to multiple categories,
                            each with multiple subcategories.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={addCategory}
                        className={addButtonClass}
                    >
                        + Category
                    </button>
                </div>

                {identity.categories.length === 0 && (
                    <p className="text-sm text-gray-500">
                        No categories added yet.
                    </p>
                )}

                {identity.categories.map((category, categoryIndex) => (

                    <div
                        key={category._id || categoryIndex}
                        className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4"
                    >

                        <div className="flex flex-col sm:flex-row gap-3">

                            <div className="flex-1">
                                <label className={labelClass}>
                                    Category Name
                                </label>

                                <input
                                    type="text"
                                    value={category.name || ""}
                                    onChange={(e) =>
                                        updateCategory(
                                            categoryIndex,
                                            "name",
                                            e.target.value
                                        )
                                    }
                                    placeholder="e.g. Waterproofing"
                                    className={inputClass}
                                />
                            </div>

                            <div className="sm:self-end">
                                <button
                                    type="button"
                                    onClick={() =>
                                        removeCategory(categoryIndex)
                                    }
                                    className={removeButtonClass}
                                >
                                    Remove Category
                                </button>
                            </div>

                        </div>

                        <div className="pl-3 border-l-2 border-gray-300 space-y-3">

                            <div className="flex justify-between items-center">
                                <p className="text-sm font-medium text-gray-700">
                                    Subcategories
                                </p>

                                <button
                                    type="button"
                                    onClick={() =>
                                        addSubcategory(categoryIndex)
                                    }
                                    className="text-sm text-blue-700 hover:underline"
                                >
                                    + Add Subcategory
                                </button>
                            </div>

                            {(category.subcategories || []).map(
                                (subcategory, subcategoryIndex) => (

                                    <div
                                        key={subcategoryIndex}
                                        className="flex gap-2"
                                    >
                                        <input
                                            type="text"
                                            value={subcategory || ""}
                                            onChange={(e) =>
                                                updateSubcategory(
                                                    categoryIndex,
                                                    subcategoryIndex,
                                                    e.target.value
                                                )
                                            }
                                            placeholder="e.g. Roof Waterproofing"
                                            className={inputClass}
                                        />

                                        <button
                                            type="button"
                                            onClick={() =>
                                                removeSubcategory(
                                                    categoryIndex,
                                                    subcategoryIndex
                                                )
                                            }
                                            className={removeButtonClass}
                                        >
                                            Remove
                                        </button>
                                    </div>

                                )
                            )}

                        </div>

                    </div>

                ))}

            </section>

            {/* PACKAGING */}

            <section className="space-y-5">

                <div className="flex justify-between items-center gap-3 border-b pb-3">

                    <h3 className="text-base font-semibold text-gray-900">
                        Packaging Options
                    </h3>

                    <button
                        type="button"
                        onClick={addPackaging}
                        className={addButtonClass}
                    >
                        + Packaging
                    </button>
                </div>

                {identity.packaging.length === 0 && (
                    <p className="text-sm text-gray-500">
                        No packaging options added.
                    </p>
                )}

                {identity.packaging.map((item, index) => (

                    <div
                        key={item._id || index}
                        className="border rounded-lg p-4 bg-gray-50 space-y-4"
                    >

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                            <div>
                                <label className={labelClass}>
                                    Packaging Type
                                </label>

                                <input
                                    type="text"
                                    value={item.type || ""}
                                    onChange={(e) =>
                                        updatePackaging(
                                            index,
                                            "type",
                                            e.target.value
                                        )
                                    }
                                    placeholder="e.g. Bucket, Bag, Drum"
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>
                                    Quantity
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={item.quantity ?? ""}
                                    onChange={(e) =>
                                        updatePackaging(
                                            index,
                                            "quantity",
                                            e.target.value === ""
                                                ? null
                                                : Number(e.target.value)
                                        )
                                    }
                                    placeholder="e.g. 20"
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>
                                    Unit
                                </label>

                                <input
                                    type="text"
                                    value={item.unit || ""}
                                    onChange={(e) =>
                                        updatePackaging(
                                            index,
                                            "unit",
                                            e.target.value
                                        )
                                    }
                                    placeholder="e.g. kg, litre"
                                    className={inputClass}
                                />
                            </div>

                        </div>

                        <div>
                            <label className={labelClass}>
                                Description
                            </label>

                            <input
                                type="text"
                                value={item.description || ""}
                                onChange={(e) =>
                                    updatePackaging(
                                        index,
                                        "description",
                                        e.target.value
                                    )
                                }
                                placeholder="e.g. 20 kg bucket"
                                className={inputClass}
                            />
                        </div>

                        <button
                            type="button"
                            onClick={() => removePackaging(index)}
                            className={removeButtonClass}
                        >
                            Remove Packaging
                        </button>

                    </div>

                ))}

            </section>

            {/* VARIANTS */}

            <section className="space-y-5">

                <div className="flex justify-between items-center gap-3 border-b pb-3">

                    <h3 className="text-base font-semibold text-gray-900">
                        Product Variants
                    </h3>

                    <button
                        type="button"
                        onClick={addVariant}
                        className={addButtonClass}
                    >
                        + Variant
                    </button>
                </div>

                {identity.variants.length === 0 && (
                    <p className="text-sm text-gray-500">
                        No variants added.
                    </p>
                )}

                {identity.variants.map((variant, variantIndex) => (

                    <div
                        key={variant._id || variantIndex}
                        className="border rounded-lg p-4 bg-gray-50 space-y-5"
                    >

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                            <div>
                                <label className={labelClass}>
                                    Variant Name
                                </label>

                                <input
                                    type="text"
                                    value={variant.name || ""}
                                    onChange={(e) =>
                                        updateVariant(
                                            variantIndex,
                                            "name",
                                            e.target.value
                                        )
                                    }
                                    placeholder="e.g. Standard"
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>
                                    Variant Code
                                </label>

                                <input
                                    type="text"
                                    value={variant.code || ""}
                                    onChange={(e) =>
                                        updateVariant(
                                            variantIndex,
                                            "code",
                                            e.target.value
                                        )
                                    }
                                    placeholder="e.g. PWR-STD"
                                    className={inputClass}
                                />
                            </div>

                        </div>

                        <div>
                            <label className={labelClass}>
                                Description
                            </label>

                            <textarea
                                value={variant.description || ""}
                                onChange={(e) =>
                                    updateVariant(
                                        variantIndex,
                                        "description",
                                        e.target.value
                                    )
                                }
                                rows={2}
                                placeholder="Variant details"
                                className={inputClass}
                            />
                        </div>

                        {/* VARIANT ATTRIBUTES */}

                        <div className="border-t pt-4 space-y-3">

                            <div className="flex justify-between items-center gap-3">

                                <div>
                                    <h4 className="text-sm font-semibold text-gray-800">
                                        Variant Attributes
                                    </h4>

                                    <p className="text-xs text-gray-500 mt-1">
                                        Define characteristics specific to this variant.
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    onClick={() =>
                                        addAttribute(variantIndex)
                                    }
                                    className="text-sm text-blue-700 hover:underline"
                                >
                                    + Add Attribute
                                </button>

                            </div>

                            {(variant.attributes || []).length === 0 && (
                                <p className="text-xs text-gray-500">
                                    No attributes added.
                                </p>
                            )}

                            {(variant.attributes || []).map(
                                (attribute, attributeIndex) => (

                                    <div
                                        key={attribute._id || attributeIndex}
                                        className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end"
                                    >

                                        <div>
                                            <label className={labelClass}>
                                                Attribute Name
                                            </label>

                                            <input
                                                type="text"
                                                value={attribute.name || ""}
                                                onChange={(e) =>
                                                    updateAttribute(
                                                        variantIndex,
                                                        attributeIndex,
                                                        "name",
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="e.g. Strength"
                                                className={inputClass}
                                            />
                                        </div>

                                        <div>
                                            <label className={labelClass}>
                                                Value
                                            </label>

                                            <input
                                                type="text"
                                                value={attribute.value || ""}
                                                onChange={(e) =>
                                                    updateAttribute(
                                                        variantIndex,
                                                        attributeIndex,
                                                        "value",
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="e.g. 40"
                                                className={inputClass}
                                            />
                                        </div>

                                        <div>
                                            <label className={labelClass}>
                                                Unit
                                            </label>

                                            <input
                                                type="text"
                                                value={attribute.unit || ""}
                                                onChange={(e) =>
                                                    updateAttribute(
                                                        variantIndex,
                                                        attributeIndex,
                                                        "unit",
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="e.g. MPa"
                                                className={inputClass}
                                            />
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                removeAttribute(
                                                    variantIndex,
                                                    attributeIndex
                                                )
                                            }
                                            className={removeButtonClass}
                                        >
                                            Remove
                                        </button>

                                    </div>

                                )
                            )}

                        </div>

                        <div className="border-t pt-4">

                            <button
                                type="button"
                                onClick={() =>
                                    removeVariant(variantIndex)
                                }
                                className={removeButtonClass}
                            >
                                Remove Variant
                            </button>

                        </div>

                    </div>

                ))}

            </section>

        </div>
    );
};

export default ProductIdentity;
